import type { Novel } from '@/lib/types';

export type GraphPosition = {
  x: number;
  y: number;
};

export const RELATIONSHIP_GRAPH_NODE_WIDTH = 132;
export const RELATIONSHIP_GRAPH_NODE_HEIGHT = 132;
const FALLBACK_HORIZONTAL_GAP = 80;
const FALLBACK_VERTICAL_GAP = 70;
const SAFE_NEW_NODE_X = 1000;

const graphEntityKeys = ['characters', 'locations', 'organizations', 'events', 'codex'] as const;

function getFallbackPosition(index: number): GraphPosition {
  return {
    x: (index % 4) * (RELATIONSHIP_GRAPH_NODE_WIDTH + FALLBACK_HORIZONTAL_GAP),
    y: Math.floor(index / 4) * (RELATIONSHIP_GRAPH_NODE_HEIGHT + FALLBACK_VERTICAL_GAP),
  };
}

function getGraphEntitySummaries(novel: Novel) {
  const summaries = novel.summaries;
  return graphEntityKeys.flatMap((key) => Object.values(summaries[key]));
}

/**
 * Finds a safe initial graph position for a newly created entity.
 *
 * If no graph positions have been saved yet, we keep using the graph's
 * fallback grid so the initial layout stays consistent.
 * Once positions exist, new entities are pushed to the far right so they
 * do not overlap current nodes.
 */
export function getNextRelationshipGraphPosition(novel: Novel): GraphPosition {
  const graphEntities = getGraphEntitySummaries(novel);
  const savedPositions = graphEntities
    .map((summary) => ('graphPosition' in summary ? summary.graphPosition : undefined))
    .filter((position): position is GraphPosition => !!position);

  if (savedPositions.length === 0) {
    return graphEntities.length > 0
      ? getFallbackPosition(graphEntities.length)
      : { x: 0, y: 0 };
  }

  const maxSavedRightEdge = savedPositions.reduce(
    (max, position) => Math.max(max, position.x + RELATIONSHIP_GRAPH_NODE_WIDTH),
    0,
  );
  const fallbackRightEdge = getFallbackPosition(Math.max(graphEntities.length, 1)).x + RELATIONSHIP_GRAPH_NODE_WIDTH;

  return {
    x: Math.max(maxSavedRightEdge + FALLBACK_HORIZONTAL_GAP * 2, fallbackRightEdge + FALLBACK_HORIZONTAL_GAP, SAFE_NEW_NODE_X),
    y: 0,
  };
}
